import React, { useState, FC, useRef, ChangeEvent } from 'react';
import { useToast } from '@common/toast';
import ConfirmModal from '@components/common/confirmModal';
import { accApplyHistoryKeys, useDeleteApplyHistory, useUpdateApplyName } from '@repository/acc';
import styles from '@components/acca/test/list/accaTestList.module.scss';
import { ApplyHistoryVO } from '@domain/vo/acca/test/ApplyHistoryListVO';
import { AccaModalType } from '@domain/constant/acca/AccaModalType';
import useClickAway from '@utils/hooks/useClickOutside';
import { queryClient } from '@utils/queryClient';

import classnames from 'classnames/bind';
import UrlUtil from '@utils/urlUtil';
const cx = classnames.bind(styles);

interface IApplyNameAreaProps {
  applyHistoryData: ApplyHistoryVO;
}

const ApplyNameArea: FC<IApplyNameAreaProps> = ({ applyHistoryData }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const Toast = useToast();

  const [isInputMode, setIsInputMode] = useState(false);
  const [applyName, setApplyName] = useState(applyHistoryData.name);

  //응시명 변경 api
  const { mutate: updateApplyNameMutate } = useUpdateApplyName(
    async () => {
      setIsInputMode(false);
    },
    (err) => {
      Toast({ type: 'error', iconType: 'info', content: err?.response?.data?.message });
      return;
    },
  );

  const handelModifyBtn = () => {
    setIsInputMode(true);
    inputRef.current?.focus();
  };

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (['NumpadEnter', 'Enter'].includes(e.code)) {
      e.preventDefault();

      updateApplyNameMutate({ sn: applyHistoryData.sn, name: applyName });
    } else if (e.code === 'Escape') {
      e.preventDefault();

      setApplyName(applyHistoryData.name);
      setIsInputMode(false);
    }
  };

  const handleFocusOut = () => {
    updateApplyNameMutate({ sn: applyHistoryData.sn, name: applyName });
  };

  const handleChangeApplyName = (e: ChangeEvent<HTMLInputElement>) => {
    setApplyName(e.target.value);
  };

  if (!isInputMode) {
    return (
      <span className={cx('badge', 'custom')} onClick={(e: React.MouseEvent<HTMLSpanElement>) => e.stopPropagation()}>
        <span className={cx('badgeText')}>{applyName}</span>
        <span className={cx('btnModify')} role="button" onClick={handelModifyBtn} tabIndex={1}>
          <span className={cx('hidden')}>modify</span>
        </span>
      </span>
    );
  }

  return (
    <span className={cx('inputArea')} onClick={(e: React.MouseEvent<HTMLSpanElement>) => e.stopPropagation()}>
      <input
        type="text"
        className={cx('input')}
        maxLength={20}
        value={applyName}
        onChange={handleChangeApplyName}
        ref={inputRef}
        onKeyDown={handleKeyDown}
        onBlur={handleFocusOut}
      />
    </span>
  );
};

interface ExternalContinueCardProps {
  applyUrl: string;
  userName: string;
  applyHistoryData: ApplyHistoryVO;
  setModalState: (type: AccaModalType) => void;
}

const ExternalContinueCard: FC<ExternalContinueCardProps> = ({
  applyUrl,
  userName,
  applyHistoryData,
  setModalState,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const Toast = useToast();

  const [optionVisible, setOptionVisible] = useState(false);

  const { mutate: deleteApplyHistoryMutate } = useDeleteApplyHistory(
    async () => {
      await queryClient.refetchQueries(accApplyHistoryKeys, { active: true, exact: true });
    },
    (err) => {
      switch (err?.response?.data?.errorCode) {
        case 'B902':
          setModalState('DELETE_APPLY_CONTINUE_ERROR');
          return;
        default:
          Toast({ type: 'error', iconType: 'info', content: err?.response?.data?.message });
          return;
      }
    },
  );

  const { sn, code } = applyHistoryData;

  useClickAway(() => setOptionVisible(false), cardRef);

  const handleApplyTest = () => {
    if (!code) return null;

    UrlUtil.openWindowWithPost(applyUrl, {
      authenticationCode: code,
      name: userName,
    });
  };

  const handleDeleteApplyHistory = () => {
    setOptionVisible(false);

    ConfirmModal({
      title: '정말 응시내역을 삭제하시겠습니까?',
      description: '응시 삭제시 해당 응시를 다시 할 수 없습니다.',
      onConfirm: () => {
        deleteApplyHistoryMutate([sn]);
      },
    });
  };

  return (
    <div className={cx('btnWrap')}>
      <a className={cx('btn')} role="button" onClick={handleApplyTest}>
        <div className={cx('textWrap')}>
          <ApplyNameArea applyHistoryData={applyHistoryData} />
          이어하기
        </div>
      </a>

      <div className={cx('optionArea')} ref={cardRef}>
        <a className={cx('btnOption')} role="button" onClick={() => setOptionVisible(!optionVisible)}>
          <span className={cx('hidden')}>option</span>
        </a>
        {optionVisible && (
          <div className={cx('menuArea')}>
            <a className={cx('btnMenu')} role="button" onClick={handleDeleteApplyHistory}>
              삭제
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExternalContinueCard;
